import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { buildProductTrackingProperties } from '@/utils/tracking/productTrackingProperties';
import { useCallback } from 'react';

/**
 * Transition Technology专题页神策埋点Hook
 * 对应Vue版本中transition-technology/index.vue的神策埋点功能
 * 迁移到React版本的transition-lenses-knowledge页面
 */
export function useTransitionTechnologyTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  const buildProductProperties = useCallback(
    (item: any) =>
      buildProductTrackingProperties(
        item,
        getPriceInfo(userInfo, item, currency),
        getPriceInfo(userInfo, item, { name: env.NEXT_PUBLIC_DEFAULT_CURRENCY }),
        currency,
      ),
    [currency, userInfo],
  );

  /**
   * 神策埋点：专题页页面浏览
   * 对应Vue版本中的SDPageView函数
   * @TrackType sensorsdata
   * @EventType TransitionKnowledgePage_View
   * @EventDesc 专题页页面浏览
   * @EventParams {
   *   refer_page_name: 前向页面名称,
   *   refer_url_path: 前向页面路径,
   * }
   * @Tapd 1010887
   */
  const trackTransitionKnowledgePageView = useCallback(() => {
    try {
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      trackEvent(
        {
          eventName: 'TransitionKnowledgePage_View',
          properties: {
            refer_page_name,
            refer_url_path,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('TransitionKnowledgePage_View tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * 神策埋点：商品坑位曝光
   * 对应Vue版本中的SDPageMkExp函数
   * @TrackType sensorsdata
   * @EventType TransitionKnowledgePageProductPits_Exp
   * @EventDesc 资源位曝光
   * @EventParams {
   *   pits_index: 坑位排序,
   *   mkt_name: 版位名称,
   *   shop_sku: 销售商品sku,
   *   business_sku: 平台sku,
   *   product_sku: 商品sku,
   * }
   * @Tapd 1012077
   */
  const trackTransitionKnowledgePageProductExp = useCallback(
    (params: { item: any; index: number }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionKnowledgePageProductPits_Exp',
            properties: {
              pits_index: params.index,
              mkt_name: '商品位',
              ...buildProductProperties(params.item),
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionKnowledgePageProductPits_Exp tracking error:', error);
      }
    },
    [buildProductProperties, trackEvent],
  );

  /**
   * 神策埋点：商品坑位点击
   * 对应Vue版本中的SDPageMktClick函数
   * @TrackType sensorsdata
   * @EventType TransitionKnowledgePageProductPits_Click
   * @EventDesc 资源位点击
   * @EventParams {
   *   pits_index: 坑位排序,
   *   mkt_name: 版位名称,
   *   shop_sku: 销售商品sku,
   *   business_sku: 平台sku,
   *   product_sku: 商品sku,
   * }
   * @Tapd 1012077
   */
  const trackTransitionKnowledgePageProductClick = useCallback(
    (params: { item: any; index: number }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionKnowledgePageProductPits_Click',
            properties: {
              pits_index: params.index,
              mkt_name: '商品位',
              ...buildProductProperties(params.item),
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionKnowledgePageProductPits_Click tracking error:', error);
      }
    },
    [buildProductProperties, trackEvent],
  );

  /**
   * 神策埋点：Banner资源位点击
   * 对应Vue版本中的SDPageMkClick函数
   * @TrackType sensorsdata
   * @EventType TransitionKnowledgePageBanner_Click
   * @EventDesc Banner资源位点击
   * @EventParams {
   *   mkt_name: 版位名称,
   *   group_id: 分组id,
   * }
   * @Tapd 1012077
   */
  const trackTransitionKnowledgePageBannerClick = useCallback(
    (params: { group_id: string | number }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionKnowledgePageBanner_Click',
            properties: {
              mkt_name: 'banner',
              group_id: `${params.group_id}`,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionKnowledgePageBanner_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：Banner资源位曝光
   * 对应Vue版本中的SDPageMkExp函数
   * @TrackType sensorsdata
   * @EventType TransitionKnowledgePageBanner_Exp
   * @EventDesc Banner资源位曝光
   * @EventParams {
   *   mkt_name: 版位名称,
   *   group_id: 分组id,
   * }
   * @Tapd 1012077
   */
  const trackTransitionKnowledgePageBannerExp = useCallback(
    (params: { group_id: string | number }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionKnowledgePageBanner_Exp',
            properties: {
              mkt_name: 'banner',
              group_id: `${params.group_id}`,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionKnowledgePageBanner_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：其他资源位曝光
   * 对应Vue版本中的SDPageMkExp函数
   * @TrackType sensorsdata
   * @EventType TransitionKnowledgePageMkt_Exp
   * @EventDesc 资源位曝光
   * @EventParams {
   *   mkt_name: 版位名称,
   * }
   * @Tapd 1012077
   */
  const trackTransitionKnowledgePageMktExp = useCallback(
    (params: { mkt_name: string }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionKnowledgePageMkt_Exp',
            properties: {
              mkt_name: params.mkt_name,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionKnowledgePageMkt_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );
  return {
    // 页面级埋点
    trackTransitionKnowledgePageView,
    // 商品级埋点
    trackTransitionKnowledgePageProductExp,
    trackTransitionKnowledgePageProductClick,
    // Banner级埋点
    trackTransitionKnowledgePageBannerClick,
    trackTransitionKnowledgePageBannerExp,
    // 其他资源位埋点
    trackTransitionKnowledgePageMktExp,
  };
}
